// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { BoundariesActions, BoundariesActionTypes } from 'actions/boundaries-actions';
import { ModelsActionTypes, ModelsActions } from 'actions/models-actions';
import { AuthActionTypes, AuthActions } from 'actions/auth-actions';
import { MLModel, ModelKind, Task } from 'cvat-core-wrapper';
import { ModelsState } from '.';

const defaultState: ModelsState = {
    initialized: false,
    fetching: false,
    creatingStatus: '',
    interactors: [],
    detectors: [],
    trackers: [],
    reid: [],
    classifiers: [],
    modelRunnerIsVisible: false,
    modelRunnerInstance: null,
    taskInferences: {},
    jobInferences: {},
    totalCount: 0,
    query: {
        page: 1,
        id: null,
        search: null,
        filter: null,
        sort: null,
    },
    previews: {},
};

export default function (state = defaultState, action: ModelsActions | AuthActions | BoundariesActions): ModelsState {
    switch (action.type) {
        case ModelsActionTypes.GET_MODELS: {
            return {
                ...state,
                fetching: true,
                query: {
                    ...state.query,
                    ...action.payload.query,
                },
            };
        }
        case ModelsActionTypes.GET_MODELS_SUCCESS: {
            return {
                ...state,
                interactors: action.payload.models.filter((model: MLModel) => (
                    model.kind === ModelKind.INTERACTOR
                )),
                detectors: action.payload.models.filter((model: MLModel) => (
                    model.kind === ModelKind.DETECTOR
                )),
                trackers: action.payload.models.filter((model: MLModel) => (
                    model.kind === ModelKind.TRACKER
                )),
                reid: action.payload.models.filter((model: MLModel) => (
                    model.kind === ModelKind.REID
                )),
                classifiers: action.payload.models.filter((model: MLModel) => (
                    model.kind === ModelKind.CLASSIFIER
                )),
                totalCount: action.payload.count,
                initialized: true,
                fetching: false,
            };
        }
        case ModelsActionTypes.GET_MODELS_FAILED: {
            return {
                ...state,
                initialized: true,
                fetching: false,
            };
        }
        case ModelsActionTypes.SHOW_RUN_MODEL_DIALOG: {
            return {
                ...state,
                modelRunnerIsVisible: true,
                modelRunnerInstance: action.payload.taskInstance,
            };
        }
        case ModelsActionTypes.CLOSE_RUN_MODEL_DIALOG: {
            return {
                ...state,
                modelRunnerIsVisible: false,
                modelRunnerInstance: null,
            };
        }
        case ModelsActionTypes.SHOW_RUN_MODEL_DIALOG_FOR_JOB: {
            return {
                ...state,
                modelRunnerIsVisible: true,
                modelRunnerInstance: action.payload.jobInstance,
            };
        }
        case ModelsActionTypes.CLOSE_RUN_MODEL_DIALOG_FOR_JOB: {
            return {
                ...state,
                modelRunnerIsVisible: false,
                modelRunnerInstance: null,
            };
        }
        case ModelsActionTypes.GET_INFERENCE_STATUS_SUCCESS: {
            const { taskInferences, jobInferences } = state;

            if (action.payload.activeInference.status === 'finished') {
                if (action.payload.instance instanceof Task) {
                    return {
                        ...state,
                        taskInferences: Object.fromEntries(
                            Object.entries(taskInferences).filter(([key]): boolean => +key !== action.payload.instance),
                        ),
                    };
                }
                return {
                    ...state,
                    jobInferences: Object.fromEntries(
                        Object.entries(jobInferences).filter(([key]): boolean => +key !== action.payload.instance),
                    ),
                };
            }

            const update: any = {};
            update[action.payload.instance.id] = action.payload.activeInference;

            if (action.payload.instance instanceof Task) {
                return {
                    ...state,
                    taskInferences: {
                        ...taskInferences,
                        ...update,
                    },
                };
            }
            return {
                ...state,
                jobInferences: {
                    ...jobInferences,
                    ...update,
                },
            };
        }
        case ModelsActionTypes.GET_INFERENCE_STATUS_FAILED: {
            const { taskInferences, jobInferences } = state;

            const update: any = {};
            update[action.payload.instance.id] = action.payload.activeInference;

            if (action.payload.instance instanceof Task) {
                return {
                    ...state,
                    taskInferences: {
                        ...taskInferences,
                        ...update,
                    },
                };
            }
            return {
                ...state,
                jobInferences: {
                    ...jobInferences,
                    ...update,
                },
            };
        }
        case ModelsActionTypes.CANCEL_INFERENCE_SUCCESS: {
            const { taskInferences, jobInferences } = state;

            if (action.payload.instance instanceof Task) {
                delete taskInferences[action.payload.instance.id];
                return {
                    ...state,
                    taskInferences: {
                        ...taskInferences,
                    },
                };
            }
            delete jobInferences[action.payload.instance.id];
            return {
                ...state,
                jobInferences: {
                    ...jobInferences,
                },
            };
        }
        case ModelsActionTypes.GET_MODEL_PREVIEW: {
            const { modelID } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [modelID]: {
                        preview: '',
                        fetching: true,
                        initialized: false,
                    },
                },
            };
        }
        case ModelsActionTypes.GET_MODEL_PREVIEW_SUCCESS: {
            const { modelID, preview } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [modelID]: {
                        preview,
                        fetching: false,
                        initialized: true,
                    },
                },
            };
        }
        case ModelsActionTypes.GET_MODEL_PREVIEW_FAILED: {
            const { modelID } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [modelID]: {
                        ...previews[modelID],
                        fetching: false,
                        initialized: true,
                    },
                },
            };
        }
        case BoundariesActionTypes.RESET_AFTER_ERROR:
        case AuthActionTypes.LOGOUT_SUCCESS: {
            return { ...defaultState };
        }
        default: {
            return state;
        }
    }
}
